import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/auction-market-car-transactions.json";
import { normalizeTransactionsList } from "../market-car/normalize";
import { getTransactionDataFiltered } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { AuctionCarTransaction } from "@/types/auctions";
import type { PaginatedList } from "@/types/entity";
import type { TransactionQueryParams } from "./types";

if (import.meta.env.MODE === "test") {
  // Mocking the API response for auction cars
  const mock = AxiosMock.getInstance();
  const uri = new RegExp("^\/carburate\/auctions\/market_cars\/.+\/transactions\/(\\?.*)?$");
  mock.onGet(uri).reply(200, data);
}

type MarketCarQueryParams = TransactionQueryParams & {
  marketCarId: string;
};

export const getAuctionMarketCarTransactions = ({
  marketCarId,
  page = 1,
  pageSize = 25,
}: MarketCarQueryParams) => {
  return api.get(
    `/carburate/auctions/market_cars/${marketCarId}/transactions/?page=${page}&page_size=${pageSize}`
  ) as Promise<PaginatedList<AuctionCarTransaction>>;
};

export const getAuctionsMarketCarTransactionsQueryOptions = ({
  marketCarId,
  page,
  pageSize,
}: MarketCarQueryParams) => {
  return queryOptions({
    queryKey: ["market-car-auctions", marketCarId, "transactions", { page, pageSize }],
    queryFn: () => getAuctionMarketCarTransactions({ marketCarId, page, pageSize }),
  });
};

type UseAuctionMarketCarTransactionsOptions = MarketCarQueryParams & {
  enabled?: boolean;
  queryConfig?: QueryConfig<typeof getAuctionMarketCarTransactions>;
};

export const useAuctionMarketCarTransactions = ({
  marketCarId,
  page,
  pageSize,
  parameters,
  enabled = true,
  queryConfig,
  sort,
}: UseAuctionMarketCarTransactionsOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<AuctionCarTransaction>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && enabled,
    ...getAuctionsMarketCarTransactionsQueryOptions({
      marketCarId,
      page: currentPage,
      pageSize: currentPageSize,
    }),
    ...queryConfig,
  });

  data = response!;
  if (isSuccess && response) {
    data = normalizeTransactionsList(response);
    if (isClientPagination) {
      data = getTransactionDataFiltered(data, { page, pageSize, parameters, sort });
    }
  }

  return { data, isSuccess, ...props };
};
